import { SERVICE_NAME } from "@/config/serviceNames";
import { requests } from ".";

interface SearchTemplatesQuery {
  skip: string;
  limit: string;
  deleted: string;
  type: string;
}

interface LabFeature {
  key: string;
  name: string;
  details: {
    heading: string;
    body: string;
  };
  liveAt: string;
  enabledAt: string;
  visibleAt: string;
  default: boolean;
  toggleHidden: boolean;
}

export const RAMP_UP_INTERVAL_OPTIONS = ["daily", "weekly", "monthly"] as const;
export type RampUpInterval = (typeof RAMP_UP_INTERVAL_OPTIONS)[number];

export const COMPANY_STATUS_OPTIONS = [
  "active",
  "active_trial",
  "active_special",
  "active_overused",
  "active_failed-payment",
  "active_trial-extension",
  "active_test-agency",
  "active_auto-cancel",
  "active_paused",
  "active_suspended",
  "inactive_failed-payment",
  "inactive",
  "inactive_auto-cancelled",
  "inactive_cancelled",
  "inactive_force-cancelled",
  "inactive_paused",
  "inactive_billing-error",
] as const;
export type CompanyStatus = (typeof COMPANY_STATUS_OPTIONS)[number];

export interface RampUpRule {
  interval: RampUpInterval;
  limit: number;
}

export interface CompanyRampUpPolicy {
  id: string;
  companyId: string;
  deleted: boolean;
  overallLimit?: number;
  applicableCompanyStatuses?: CompanyStatus[];
  rules?: RampUpRule[];
  description?: string;
}

export interface CompanyRampUpPolicyPayload {
  deleted?: boolean;
  overallLimit?: number;
  applicableCompanyStatuses?: CompanyStatus[];
  rules?: RampUpRule[];
  description?: string;
}

const BLOCKED_FIELD_KEYS = new Set(["__proto__", "constructor", "prototype"]);
const ALLOWED_FIELD_PATTERN = /^[a-zA-Z][a-zA-Z0-9_.]{0,63}$/;
const requireNonEmptyId = (id: string, label: string) => {
  const trimmed = id.trim();
  if (!trimmed) {
    throw new Error(`${label} is required.`);
  }
  return encodeURIComponent(trimmed);
};

const isValidFieldKey = (field: string) =>
  !!field &&
  !field.startsWith("$") &&
  !BLOCKED_FIELD_KEYS.has(field) &&
  ALLOWED_FIELD_PATTERN.test(field) &&
  field
    .split(".")
    .every(
      (part) =>
        !!part &&
        !part.startsWith("$") &&
        !BLOCKED_FIELD_KEYS.has(part) &&
        /^[a-zA-Z][a-zA-Z0-9_]{0,63}$/.test(part)
    );

const buildLocationUpdatePayload = (fields: Record<string, unknown>) => {
  const entries = Object.entries(fields || {});
  if (!entries.length) throw new Error("At least one field is required.");

  const sanitized: Record<string, unknown> = {};
  for (const [rawKey, value] of entries) {
    const field = rawKey.trim();
    if (!isValidFieldKey(field)) {
      throw new Error(`Invalid API field: ${rawKey}`);
    }
    sanitized[field] = value;
  }

  return { fields: sanitized };
};

const LocationsService = {
  getLocationById: (locationId: string) =>
    requests(SERVICE_NAME.LOCATIONS_API).get(locationId),

  removeLocationDomain: (locationId: string) => {
    const safeLocationId = encodeURIComponent(locationId.trim());
    return requests(SERVICE_NAME.LOCATIONS_API).put(`${safeLocationId}`, {
      domain: "",
    });
  },

  addLocationDomain: (locationId: string, domain: string) => {
    const safeLocationId = encodeURIComponent(locationId.trim());
    return requests(SERVICE_NAME.LOCATIONS_API).put(`${safeLocationId}`, {
      domain: domain.trim().toLowerCase(),
    });
  },

  updateLocationField: (
    locationId: string,
    fields: Record<string, unknown>
  ) => {
    const safeLocationId = encodeURIComponent(locationId.trim());
    return requests(SERVICE_NAME.LOCATIONS_API).patch(
      `support/locations/${safeLocationId}/fields`,
      buildLocationUpdatePayload(fields)
    );
  },

  searchTemplates: (locationId: string, params: SearchTemplatesQuery) =>
    requests(SERVICE_NAME.LOCATIONS_API).get(`${locationId}/templates`, {
      params,
    }),

  restoreLocation: (locationId: string) =>
    requests(SERVICE_NAME.LOCATIONS_API).put(`${locationId}/restore-location`),

  migrateCustomFields: (locationId: string) =>
    requests(SERVICE_NAME.LOCATIONS_API).post(
      `${locationId}/customFields/migrate`
    ),

  updateLocationEmail: (id: string, email: string) =>
    requests(SERVICE_NAME.LOCATIONS_API).put(`${id}`, { email }),

  createCompanyRampupPolicy: (
    companyId: string,
    payload: CompanyRampUpPolicyPayload
  ) => {
    const safeCompanyId = requireNonEmptyId(companyId, "companyId");
    return requests(SERVICE_NAME.LOCATIONS_API).post(
      `companies/${safeCompanyId}/rampup-policies`,
      payload
    );
  },

  listCompanyRampupPolicies: (companyId: string) => {
    const safeCompanyId = requireNonEmptyId(companyId, "companyId");
    return requests(SERVICE_NAME.LOCATIONS_API).get(
      `companies/${safeCompanyId}/rampup-policies`
    );
  },

  getCompanyRampupPolicy: (companyId: string, policyId: string) => {
    const safeCompanyId = requireNonEmptyId(companyId, "companyId");
    const safePolicyId = requireNonEmptyId(policyId, "policyId");
    return requests(SERVICE_NAME.LOCATIONS_API).get(
      `companies/${safeCompanyId}/rampup-policies/${safePolicyId}`
    );
  },

  updateCompanyRampupPolicy: (
    companyId: string,
    policyId: string,
    payload: CompanyRampUpPolicyPayload
  ) => {
    const safeCompanyId = requireNonEmptyId(companyId, "companyId");
    const safePolicyId = requireNonEmptyId(policyId, "policyId");
    return requests(SERVICE_NAME.LOCATIONS_API).put(
      `companies/${safeCompanyId}/rampup-policies/${safePolicyId}`,
      payload
    );
  },

  deleteCompanyRampupPolicy: (companyId: string, policyId: string) => {
    const safeCompanyId = requireNonEmptyId(companyId, "companyId");
    const safePolicyId = requireNonEmptyId(policyId, "policyId");
    return requests(SERVICE_NAME.LOCATIONS_API).delete(
      `companies/${safeCompanyId}/rampup-policies/${safePolicyId}`
    );
  },
};
export default LocationsService;
